import { BUILD_VERSION } from './api';
import { displayMode } from './displayMode';
import { useInstallPrompt } from './installPrompt';
import { platformInfo } from './platform';

/**
 * The "I'm already in the app" sheet shows these lines and can copy them for the group chat.
 * It is meant to be pasted to other people, so it holds only what helps to work out why a phone
 * is shown the install gate: display-mode answers, the browser family, which web features exist.
 * It never holds the address (an invite code lives in it), cookies, storage contents, the
 * server's answers, or anything about the person. The referrer is cut to its origin and the user
 * agent to 160 characters.
 */

/** What the lines are made from: plain values, so a test can feed any phone. */
export interface DiagSource {
  version: string;
  how: string;
  media: { standalone: boolean; fullscreen: boolean; minimalUi: boolean; browser: boolean };
  navigatorStandalone: boolean | null;
  referrer: string;
  gate: string;
  inAppBrowser: boolean;
  userAgent: string;
  viewport: string;
  language: string;
  online: boolean;
  secureContext: boolean;
  features: { serviceWorker: boolean; push: boolean; notifications: string; wakeLock: boolean; installPrompt: boolean };
}

/** "https://l.instagram.com/?u=secret#x" becomes "https://l.instagram.com". Anything else is "(none)". */
export function originOnly(url: string): string {
  if (!url) return '(none)';
  try {
    const u = new URL(url);
    return /^https?:$/.test(u.protocol) ? u.origin : `${u.protocol}//`;
  } catch {
    return '(none)';
  }
}

const yes = (b: boolean) => (b ? 'yes' : 'no');

export function buildDiagnostics(s: DiagSource): Record<string, string> {
  return {
    app: s.version,
    'display mode': s.how,
    'mq standalone': yes(s.media.standalone),
    'mq fullscreen': yes(s.media.fullscreen),
    'mq minimal-ui': yes(s.media.minimalUi),
    'mq browser': yes(s.media.browser),
    'navigator.standalone': s.navigatorStandalone === null ? 'n/a' : yes(s.navigatorStandalone),
    referrer: originOnly(s.referrer),
    'gate steps': s.gate,
    'in-app browser': yes(s.inAppBrowser),
    'user agent': s.userAgent.slice(0, 160),
    viewport: s.viewport,
    language: s.language,
    online: yes(s.online),
    'secure context': yes(s.secureContext),
    'service worker': yes(s.features.serviceWorker),
    push: yes(s.features.push),
    notifications: s.features.notifications,
    'wake lock': yes(s.features.wakeLock),
    'install prompt': yes(s.features.installPrompt),
  };
}

/** Reads this phone. Every read is guarded: a missing API reads as "no", never as an error. */
export function collectDiagnostics(): Record<string, string> {
  const mq = (q: string) => {
    try {
      return window.matchMedia(`(display-mode: ${q})`).matches;
    } catch {
      return false;
    }
  };
  const nav = navigator as Navigator & { standalone?: boolean };
  const info = platformInfo();
  return buildDiagnostics({
    version: BUILD_VERSION,
    how: displayMode().how,
    media: {
      standalone: mq('standalone'),
      fullscreen: mq('fullscreen'),
      minimalUi: mq('minimal-ui'),
      browser: mq('browser'),
    },
    navigatorStandalone: typeof nav.standalone === 'boolean' ? nav.standalone : null,
    referrer: document.referrer,
    gate: info.gate,
    inAppBrowser: info.inAppBrowser,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`,
    language: navigator.language,
    online: navigator.onLine !== false,
    secureContext: window.isSecureContext,
    features: {
      serviceWorker: 'serviceWorker' in navigator,
      push: 'PushManager' in window,
      notifications: typeof Notification === 'undefined' ? 'n/a' : Notification.permission,
      wakeLock: 'wakeLock' in navigator,
      installPrompt: useInstallPrompt.getState().event !== null,
    },
  });
}

/** One line per key, for the clipboard. */
export function diagnosticsText(d: Record<string, string>): string {
  return Object.entries(d)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
}
